import { useSelectFunnelStagesByType } from '@user/store/salesFunnel'
import { useActions } from '@user/components/Leads/LeadCard/hooks'
import { ParentHeader as Container } from '@user/components/Leads/LeadCard/styling'
import { AttachmentsButton } from '@user/components/Leads/LeadCard/LeadAttachmentsDrawer/AttachmentIconButton'
import {
  NotesButton,
  NotesDrawer,
} from '@user/components/Leads/LeadCard/NotesDrawer'
import { LeadStatusSelector } from '@user/components/Leads/LeadCard/LeadStatusSelector'
import { ConvertToOpportunityButton } from '@user/components/Leads/LeadCard/ConvertToOpportunity'
import { AlertDialogSlide, MoreButton } from '@sales-center/components'
import { LeadAttachmentsDrawer } from '@user/components/Leads/LeadCard/LeadAttachmentsDrawer'
import { ReOpenLeadDialog } from '@user/components/Leads/ReOpenDialog/ReOpenLeadDialog'
import { ReassignDialog } from '@user/components/Leads/LeadCard/ReassignDialog'
import * as React from 'react'
import { PopoverOrigin } from '@mui/material/Popover'
import type { LeadParentHeaderProps } from '@user/components/Leads/LeadCard/typings'
import { NextActionDate } from '@user/components/EntityCards/atoms/NextActionDate/NextActionDate'
import { ENTITY_TYPES } from '@sales-center/common'
import Hidden from '@mui/material/Hidden'
import Stack from '@mui/material/Stack'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'

const moreButtonSettings: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

export function LeadParentHeader(props: LeadParentHeaderProps) {
  const {
    detailPage,
    lead: { salesFunnelId, id, nextActionDate },
    canUpdate,
  } = props

  const salesFunnelStages = useSelectFunnelStagesByType(salesFunnelId, 'Lead')

  const {
    isDisqualified,
    notesState: { notesMenuItem, ...notesState },
    deleteState: { deleteMenuItem, ...deleteState },
    attachmentsState: { attachmentsMenuItem, ...attachmentsState },
    reopenState: { reopenMenuItem, ...reopenState },
    reassignState: { reassignMenuItem, ...reassignState },
  } = useActions({ ...props, salesFunnelStages })

  const { isAdmin } = useSelector(selectCurrentUser)

  return (
    <>
      <Container detailPage={detailPage}>
        <Hidden lgDown>
          <Stack direction="row" spacing={2}>
            <NextActionDate
              id={id}
              nextActionDate={nextActionDate}
              entity={ENTITY_TYPES.Lead}
              disabled={!canUpdate}
            />
            {!!detailPage && <AttachmentsButton {...attachmentsState} />}
          </Stack>
        </Hidden>

        {/* ---------- actions ---------- */}
        <Stack direction="row" spacing={1.2}>
          {detailPage && <NotesButton {...props} {...notesState} />}

          <LeadStatusSelector
            {...props}
            isDisqualified={isDisqualified}
            salesFunnelStages={salesFunnelStages}
          />

          <ConvertToOpportunityButton
            {...props}
            salesFunnelStages={salesFunnelStages}
          />

          <MoreButton
            anchorOrigin={moreButtonSettings}
            dataTestId={`leadDetailMoreBtn-${id}`}
            transformOrigin={moreButtonSettings}
            options={[
              { ...notesMenuItem, dataTestId: `leadDetailNotesOption-${id}` },
              { ...deleteMenuItem, dataTestId: `leadDetailDeleteOption-${id}` },
              {
                ...reassignMenuItem,
                dataTestId: `leadDetailReassignOption-${id}`,
              },
              { ...reopenMenuItem, dataTestId: `leadDetailReopenOption-${id}` },
              {
                ...attachmentsMenuItem,
                dataTestId: `leadDetailAttachmentsOption-${id}`,
              },
            ]}
          />
        </Stack>
      </Container>

      {/* DIALOGS */}
      <NotesDrawer {...props} {...notesState} />
      <LeadAttachmentsDrawer {...props} {...attachmentsState} />
      <AlertDialogSlide {...deleteState} />
      {reopenState.salesTeamLoadingStatus === 'success' && (
        <ReOpenLeadDialog {...reopenState} />
      )}
      <ReassignDialog
        {...props}
        {...reassignState}
        showInactiveUsers={isDisqualified && isAdmin}
      />
    </>
  )
}
